import { z } from 'zod'

/**
 * Household logic shared by API, web and mobile (Phase 2 shared packages).
 */

export const HOUSEHOLD_ROLES = ['adult', 'child'] as const
export type HouseholdRole = (typeof HOUSEHOLD_ROLES)[number]

export interface HouseholdMemberName {
  lastName: string
  role: string
}

/**
 * Derives a display label from member names when no custom one is set:
 * one shared surname → "Okafor Family"; mixed surnames → "Okafor & Lin"
 * (adults' surnames first, in roster order, at most three before "…").
 */
export function deriveHouseholdLabel(members: HouseholdMemberName[]): string {
  if (members.length === 0) return 'Empty household'

  const ordered = [
    ...members.filter((m) => m.role === 'adult'),
    ...members.filter((m) => m.role !== 'adult'),
  ]
  const surnames: string[] = []
  for (const m of ordered) {
    const trimmed = m.lastName.trim()
    if (trimmed !== '' && !surnames.includes(trimmed)) surnames.push(trimmed)
  }

  if (surnames.length === 0) return 'Empty household'
  if (surnames.length === 1) return `${surnames[0]} Family`
  const shown = surnames.slice(0, 3).join(' & ')
  return surnames.length > 3 ? `${shown}…` : shown
}

export const createHouseholdSchema = z.object({
  name: z
    .string()
    .trim()
    .max(200)
    .transform((v) => (v === '' ? null : v))
    .nullish(),
  members: z
    .array(
      z.object({
        personId: z.uuid(),
        role: z.enum(HOUSEHOLD_ROLES).optional(),
      }),
    )
    .min(1, 'A household needs at least one member')
    .max(30),
})
export type CreateHouseholdInput = z.infer<typeof createHouseholdSchema>
